import { Text } from '@timelyapp/react';
import classNames from 'classnames';
import HourTags from 'components/HourTags';
import { Checkmark, Lock } from 'components/Icons';
import { Td, Tr } from 'components/ui-blocks/table';
import React from 'react';
import { prefixHexColor } from 'utils/colors';
import { getProjectColor } from 'utils/project';
import { getDepthStyle, hasHourDetails } from '../helpers';
import styles from '../TableWidget.module.scss';
import type { TableWidgetBodyRowProps } from './TableWidgetBodyRow';

const TableWidgetHourDetailsRow: React.FC<TableWidgetBodyRowProps> = ({
  columns,
  data,
  depth,
  hasBatchSelect,
  onEdit,
  readOnly,
}) => {
  const { hasAny, hasProject, hasTags, hasNote, hasBilledState, hasInvoicedState, hasLockedState } =
    hasHourDetails(data, columns);

  if (!hasAny) return null;

  const style = getDepthStyle(!hasBatchSelect && readOnly ? (depth ?? 1) - 1 : depth);
  const indent = data.avatar || data.emoji ? 80 : 64;
  style.paddingLeft = +(style.paddingLeft ?? 0) + indent;

  return (
    <Tr
      data-testid="tablewidgethourdetails"
      className={classNames(styles.hourDetailsRow, styles.note, {
        [styles.billed]: data.isBilled || data.isInvoiced || data.isLocked,
        [styles.readOnly]: readOnly,
      })}
      depth={depth}
      onClick={() => onEdit?.()}
    >
      <Td colSpan={columns.length * 2} style={style} innerClass={styles.inner}>
        {(hasProject || hasTags) && (
          <HourTags tags={hasTags ? data.labels : undefined} className={styles.tags}>
            {hasProject && (
              <li className={styles.project} data-testid="hour-details_project-name">
                <span
                  className={styles.color}
                  style={{ backgroundColor: prefixHexColor(getProjectColor(data.project!)) }}
                />
                {data.project!.name}
              </li>
            )}
          </HourTags>
        )}
        {(hasNote || hasBilledState || hasInvoicedState || hasLockedState) && (
          <div className={styles.note}>
            {hasBilledState && !hasInvoicedState && (
              <span
                className={classNames(styles.statusLabel, styles.isBilled)}
                data-testid="hour-details_billed-indicator"
              >
                <Checkmark className={styles.icon} />
                Billed
              </span>
            )}
            {hasInvoicedState && (
              <span
                className={classNames(styles.statusLabel, styles.isBilled)}
                data-testid="hour-details_billed-via-invoice-indicator"
              >
                <Checkmark className={styles.icon} />
                Billed via invoice
              </span>
            )}
            {hasLockedState && (
              <span
                className={classNames(styles.statusLabel, styles.isLocked)}
                data-testid="hour-details_locked-indicator"
              >
                <Lock className={styles.icon} />
                Locked
              </span>
            )}
            {(hasBilledState || hasInvoicedState || hasLockedState) && hasNote && ' — '}
            <Text size="xsmall" data-testid="hour-details_description">
              {hasNote && data.description}
            </Text>
          </div>
        )}
      </Td>
    </Tr>
  );
};

export default TableWidgetHourDetailsRow;
